import { Table, TableBody, TableCell, TableContainer, TableRow, colors } from '@mui/material';
import { DATA_COLORS } from '../../../config/theme/theme';
import { AnimatedNumber } from '../../components/AnimatedNumber';
import { Rectangle } from '../../components/Rectangle';
import { TypographyOverflow } from '../../components/TypographyOverflow';
import { useTabbable } from '../../hooks/useTabbable';
import { InventoryLine } from '../models/InventoryLine';

const getSectorColor = (index: number) => {
  return DATA_COLORS[index % DATA_COLORS.length];
};

type Props = {
  lines: InventoryLine[];
  onMouseEnter: (index: number) => void;
  onMouseLeave: () => void;
  activeIndex?: number;
  disableAnimation?: boolean;
};

export const ChartLegend = ({ lines, onMouseEnter, onMouseLeave, activeIndex, disableAnimation = false }: Props) => {
  const [ref, onKeyDown] = useTabbable({ direction: 'vertical', selector: '[data-role="chart-legend-line"]' });

  return (
    <TableContainer sx={{ height: '100%' }} component="div" ref={ref} onKeyDown={onKeyDown}>
      <Table
        role="presentation"
        data-role="chart-legend-container"
        size="small"
        stickyHeader
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          tr: {
            '&:hover, &.Mui-selected, &.Mui-selected:hover, &:focus': {
              backgroundColor: 'transparent',
              'td, th': {
                backgroundColor: colors.blue[50],
              },
            },
          },
          tbody: {
            td: {
              '&:nth-of-type(1), &:nth-of-type(2)': { width: '60px' },
            },
            tr: {
              'th:first-of-type': {
                borderTopLeftRadius: '18px',
                borderBottomLeftRadius: '18px',
              },
              'td:last-of-type': {
                borderTopRightRadius: '18px',
                borderBottomRightRadius: '18px',
              },
            },
            'td, th': { border: 0 },
          },
          'td, th': { p: 0.75 },
        }}
      >
        <TableBody>
          {lines.map((line: InventoryLine, index: number) => {
            const percentage = line.getPercentage();
            const count = line.getCount();
            const name = line.getName();
            return (
              <TableRow
                data-role="chart-legend-line"
                tabIndex={index === 0 ? 0 : -1}
                hover
                selected={activeIndex === index}
                key={name}
                onMouseEnter={() => onMouseEnter(index)}
                onMouseLeave={() => onMouseLeave()}
              >
                <TableCell component="th" scope="row">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Rectangle color={getSectorColor(index)} />
                    <TypographyOverflow>{name}</TypographyOverflow>
                  </div>
                </TableCell>
                <TableCell align="right">
                  {percentage !== null ? <AnimatedNumber value={percentage} suffix={'%'} decimals={1} disableAnimation={disableAnimation} /> : <>-</>}
                </TableCell>
                <TableCell align="right">
                  <AnimatedNumber value={count} disableAnimation={disableAnimation} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
